import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectDropdownProps<T> {
    options: T[];
    onSelect: (value: string) => void;
    getValue: (option: T) => string;
    getDisplayText: (option: T) => string;
    getGroup?: (option: T) => string;
    placeholder?: string;
    dropdownContainerClassName?: string;
    isValidOption?: (option: T) => boolean;
    selectedValue?: string;
    testId?: string;
    disabled?: boolean;
    showSearch?: boolean;
    searchPlaceholder?: string;
}

const SelectDropdownInline = <T,>({
    options,
    onSelect,
    getValue,
    getDisplayText,
    getGroup,
    placeholder = 'Select an option',
    dropdownContainerClassName = '',
    isValidOption = () => true,
    selectedValue = '',
    testId = 'select',
    disabled = false,
    showSearch = false,
    searchPlaceholder = 'Search...',
}: SelectDropdownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        if (!isValidOption(option)) return false;
        const displayText = getDisplayText(option).toLowerCase();
        return displayText.includes(searchTerm.toLowerCase());
    });

    // Grouped options logic
    type GroupedOptions = { [group: string]: T[] };
    const groupedOptions: GroupedOptions = getGroup
        ? filteredOptions.reduce((acc, option) => {
            const group = getGroup(option);
            if (!acc[group]) acc[group] = [];
            acc[group].push(option);
            return acc;
        }, {} as GroupedOptions)
        : { '': filteredOptions };
    // Flattened list for keyboard navigation
    const flatOptions: { option: T; group: string; isGroupHeader: boolean }[] = [];
    Object.entries(groupedOptions).forEach(([group, opts]) => {
        if (group) {
            flatOptions.push({ option: null as any, group, isGroupHeader: true });
        }
        opts.forEach(option => {
            flatOptions.push({ option, group, isGroupHeader: false });
        });
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setSearchTerm('');
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && showSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, showSearch]);


    // Set initial highlight to selected option when opening dropdown
    useEffect(() => {
        if (isOpen && selectedValue) {
            const selectedIndex = flatOptions.findIndex(
                (item) => !item.isGroupHeader && getValue(item.option) === selectedValue
            );
            if (selectedIndex >= 0) {
                setHighlightedIndex(selectedIndex);
            }
        }
    }, [isOpen, selectedValue, options, getValue]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                if (selectedValue) {
                    const selectedIndex = flatOptions.findIndex(
                        (item) => !item.isGroupHeader && getValue(item.option) === selectedValue
                    );
                    if (selectedIndex >= 0) {
                        setHighlightedIndex(selectedIndex);
                    }
                }
            }
            return;
        }

        const nextIndex = (start: number, dir: 1 | -1) => {
            let idx = start;
            do {
                idx = (idx + dir + flatOptions.length) % flatOptions.length;
            } while (flatOptions[idx]?.isGroupHeader && idx !== start);
            return idx;
        };
        switch (e.key) {
            case 'ArrowUp': {
                e.preventDefault();
                if (flatOptions.length === 0) return;
                let newIndex = highlightedIndex;
                do {
                    newIndex = nextIndex(newIndex, -1);
                } while (flatOptions[newIndex]?.isGroupHeader && newIndex !== highlightedIndex);
                setHighlightedIndex(newIndex);
                break;
            }
            case 'ArrowDown': {
                e.preventDefault();
                if (flatOptions.length === 0) return;
                let newIndex = highlightedIndex;
                do {
                    newIndex = nextIndex(newIndex, 1);
                } while (flatOptions[newIndex]?.isGroupHeader && newIndex !== highlightedIndex);
                setHighlightedIndex(newIndex);
                break;
            }
            case 'Enter':
            case ' ': {
                e.preventDefault();
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < flatOptions.length &&
                    !flatOptions[highlightedIndex].isGroupHeader
                ) {
                    selectOption(flatOptions[highlightedIndex].option);
                }
                break;
            }
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setSearchTerm('');
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setSearchTerm('');
                break;
        }
    };

    // Handle mouse enter on option
    const handleMouseEnter = (index: number) => {
        setHighlightedIndex(index);
    };

    // Select option
    const selectOption = (option: T) => {
        onSelect(getValue(option));
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchTerm('');
    };

    // Find the selected option for display
    const selectedOption = options.find(option => getValue(option) === selectedValue);

    return (
        <div
            className="relative w-full"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            id={`${testId}-container`}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${testId}-options-list`}
        >
            {/* Selected Option Display */}
            <div
                id={`${testId}-trigger`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full px-3 py-2 border border-border rounded-md flex items-center ${disabled ? 'cursor-not-allowed bg-gray-100' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-black focus:border-black z-9 bg-white`}
            >
                <span
                    id={`${testId}-selected-value`}
                    className={`text-sm ${!selectedValue || !selectedOption ? 'text-gray-500' : disabled ? 'text-gray-400' : 'text-gray-700'} mr-1.5`}
                >
                    {selectedOption ? getDisplayText(selectedOption) : placeholder}
                </span>
                <ChevronDown
                    id={`${testId}-chevron`}
                    className={`w-4 h-4 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${disabled ? 'text-gray-400' : ''}`}
                />
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div
                    id={`${testId}-dropdown`}
                    className={`absolute top-full left-0 w-full bg-white border border-border rounded-md shadow-lg z-10 ${dropdownContainerClassName}`}
                >
                    {/* Search Input */}
                    {showSearch && (
                        <div
                            id={`${testId}-search-container`}
                            className="p-2 border-b border-gray-200"
                        >
                            <input
                                ref={searchInputRef}
                                id={`${testId}-search-input`}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="w-full px-3 py-2 border text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                    // Allow Enter, Escape, and Arrow keys to bubble to parent for navigation/selection
                                    // Stop propagation for all other keys (space, letters, numbers, etc.) for typing
                                    if (e.key !== 'Enter' && e.key !== 'Escape' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Options List */}
                    <ul
                        ref={optionsRef}
                        className="max-h-[130px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                        role="listbox"
                        id={`${testId}-options-list`}
                        aria-label="Options"
                    >
                        {flatOptions.length === 0 ? (
                            <li
                                id={`${testId}-no-options`}
                                className="px-3 py-1 text-gray-500 italic truncate text-start text-sm"
                            >
                                No options available
                            </li>
                        ) : (
                            flatOptions.map((item, index) => {
                                if (item.isGroupHeader) {
                                    return (
                                        <li key={`group-${item.group}`} className="px-3 py-2 text-sm font-semibold bg-gray-50 sticky top-0 z-10">
                                            {item.group}
                                        </li>
                                    );
                                }
                                const value = getValue(item.option);
                                const isSelected = selectedValue === value;
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <li
                                        key={value}
                                        id={`${testId}-option-${index}`}
                                        onClick={() => selectOption(item.option)}
                                        onMouseEnter={() => handleMouseEnter(index)}
                                        className={`px-3 py-1 cursor-pointer truncate whitespace-nowrap text-start flex justify-between text-sm transition-colors duration-150
                                            ${isHighlighted ? 'bg-gray-200' : ''}
                                            ${isSelected ? 'font-semibold' : ''}
                                            hover:bg-gray-200
                                        `}
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-current={isHighlighted}
                                    >
                                        {getDisplayText(item.option)}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SelectDropdownInline;
